import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, CalendarDays, ChevronDown, MapPin, Search } from 'lucide-react';
import { EVENT_TYPES, getUpcomingKeralaEvents } from '../lib/deals';
import { homeStructuredData, usePageSeo } from '../lib/seo';
import { monthLabel } from '../lib/time';
import CookieConsent from '../components/CookieConsent';
import EventCard from '../components/EventCard';
import JoinList from '../components/JoinList';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

const ALL_CITIES = 'All of Kerala';
const ALL_TYPES = 'All events';
const DAY = 24 * 60 * 60 * 1000;
const windows = [
  { label: 'Any date', days: null },
  { label: 'Next 7 days', days: 7 },
  { label: 'Next 30 days', days: 30 },
];

const endOf = (event) => new Date(event.expires_at || event.starts_at).getTime();

export default function KeralaEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(() => { try { return localStorage.getItem('malluland-kerala-city') || ALL_CITIES; } catch { return ALL_CITIES; } });
  const [type, setType] = useState(ALL_TYPES);
  const [windowDays, setWindowDays] = useState(null);
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(() => Date.now());
  usePageSeo({
    title: "Malluland | What's On in Kerala",
    description: 'Festivals, concerts, film, sport, and literature events across Kerala, with dates, venues, and ticket links.',
    structuredData: homeStructuredData,
  });

  useEffect(() => {
    getUpcomingKeralaEvents()
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 15 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);

  const upcoming = useMemo(() => events.filter((event) => event.starts_at && endOf(event) > now), [events, now]);
  const cities = useMemo(() => Array.from(new Set(upcoming.map((event) => event.city).filter(Boolean))).sort(), [upcoming]);
  const types = useMemo(() => EVENT_TYPES.filter((item) => upcoming.some((event) => event.event_type === item)), [upcoming]);

  useEffect(() => {
    if (!loading && city !== ALL_CITIES && !cities.includes(city)) setCity(ALL_CITIES);
  }, [loading, cities, city]);

  const selectCity = (value) => {
    setCity(value);
    try { localStorage.setItem('malluland-kerala-city', value); } catch { /* private browsing, etc. */ }
  };

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return upcoming.filter((event) => {
      if (city !== ALL_CITIES && event.city !== city) return false;
      if (type !== ALL_TYPES && event.event_type !== type) return false;
      if (windowDays && new Date(event.starts_at).getTime() > now + windowDays * DAY) return false;
      return !query || [event.title, event.merchant, event.description, event.neighbourhood, event.city].filter(Boolean).join(' ').toLowerCase().includes(query);
    });
  }, [upcoming, city, type, windowDays, search, now]);

  // Events already under way (e.g. a multi-day festival) sort to the top; the rest by start date, grouped by month.
  const months = useMemo(() => {
    const groups = [];
    visible.forEach((event) => {
      const started = new Date(event.starts_at).getTime() <= now;
      const label = started ? 'Happening now' : monthLabel(event.starts_at);
      const group = groups.find((item) => item.label === label);
      if (group) group.events.push(event); else groups.push({ label, events: [event] });
    });
    return groups.sort((a, b) => (b.label === 'Happening now') - (a.label === 'Happening now'));
  }, [visible, now]);

  const filtered = city !== ALL_CITIES || type !== ALL_TYPES || windowDays || search.trim();
  const clearFilters = () => { selectCity(ALL_CITIES); setType(ALL_TYPES); setWindowDays(null); setSearch(''); };

  return <div style={styles.page}>
    <SiteHeader />

    <main>
      <section style={styles.hero}>
        <p style={styles.pill}><MapPin size={15} /> Keralam</p>
        <h1 style={styles.heading}>What&rsquo;s on <em style={styles.headingAccent}>in Kerala.</em></h1>
        <p style={styles.lede}>Festivals, concerts, film, sport, and literature events from Kasaragod to Thiruvananthapuram. We check every listing for its date, venue, and ticket link.</p>
        {!loading && upcoming.length > 0 && <p style={styles.count}><CalendarDays size={17} /> {upcoming.length} upcoming {upcoming.length === 1 ? 'event' : 'events'} in {cities.length} {cities.length === 1 ? 'city' : 'cities'}</p>}
      </section>

      <section style={styles.eventsSection} aria-labelledby="events-heading">
        <div style={styles.sectionTop}>
          <div><p style={styles.eyebrow}>The calendar</p><h2 id="events-heading" style={styles.sectionTitle}>Upcoming events</h2></div>
          <div style={styles.controls}>
            <label style={styles.searchBox}><Search size={17} /><span style={styles.srOnly}>Search events</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search events" style={styles.searchInput} /></label>
            <Dropdown label="Filter by city" value={city} options={[ALL_CITIES, ...cities]} onChange={selectCity} />
            <Dropdown label="Filter by type" value={type} options={[ALL_TYPES, ...types]} onChange={setType} />
          </div>
        </div>
        <div style={styles.chips} role="group" aria-label="Filter by date">
          {windows.map((item) => <button type="button" key={item.label} aria-pressed={windowDays === item.days} onClick={() => setWindowDays(item.days)} style={{ ...styles.chip, ...(windowDays === item.days ? styles.chipActive : {}) }}>{item.label}</button>)}
        </div>

        {loading ? <p style={styles.status}>Loading the calendar...</p>
          : visible.length === 0 ? <EmptyState filtered={filtered} onClear={clearFilters} />
          : months.map((group) => <div key={group.label} style={styles.month}>
            <h3 style={styles.monthTitle}>{group.label}</h3>
            <div style={styles.grid}>{group.events.map((event) => <EventCard event={event} key={event.id} />)}</div>
          </div>)}
      </section>

      <JoinList cities={cities} />
    </main>

    <SiteFooter />
    <CookieConsent />
  </div>;
}

function Dropdown({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  return <div style={styles.selectBox}>
    <button type="button" onClick={() => setOpen((current) => !current)} aria-haspopup="listbox" aria-expanded={open} aria-label={label} style={styles.selectButton}>
      {value}<ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
    </button>
    {open && <div role="listbox" aria-label={label} style={styles.selectMenu}>{options.map((item) => <button type="button" role="option" aria-selected={value === item} key={item} onClick={() => { onChange(item); setOpen(false); }} style={{ ...styles.selectOption, ...(value === item ? styles.selectOptionActive : {}) }}>{item}</button>)}</div>}
  </div>;
}

function EmptyState({ filtered, onClear }) {
  return <div style={styles.empty}>
    <CalendarDays size={30} />
    <h3>{filtered ? 'No events match those filters' : 'The calendar is being filled in.'}</h3>
    <p>{filtered ? 'Try a different city, type, or date range.' : 'We only list events we have checked. Follow Malluland to hear about new ones first.'}</p>
    {filtered ? <button type="button" onClick={onClear} style={styles.emptyButton}>Clear filters</button>
      : <a href="https://www.instagram.com/mallulandonline/" target="_blank" rel="noopener noreferrer" style={styles.emptyLink}>Follow Malluland <ArrowUpRight size={16} /></a>}
  </div>;
}

const styles = {
  page: { minHeight: '100vh', background: '#fff', color: '#111', fontFamily: 'Gabarito, system-ui, sans-serif' },
  hero: { maxWidth: 1180, margin: '0 auto', padding: '5.5rem 1.5rem 4.5rem', borderBottom: '1px solid #ddd' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #ccc', borderRadius: 999, color: '#333', padding: '0.45rem 0.75rem', fontSize: '0.84rem', margin: 0 },
  heading: { fontSize: 'clamp(3.6rem, 9vw, 7.3rem)', lineHeight: 0.92, letterSpacing: '-0.035em', margin: '1.6rem 0 1.35rem', fontWeight: 800 }, headingAccent: { color: '#111', fontStyle: 'normal', letterSpacing: '-0.025em' },
  lede: { color: '#333', fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', maxWidth: 600, lineHeight: 1.45, margin: 0 },
  count: { display: 'flex', alignItems: 'center', gap: 7, marginTop: '2.3rem', marginBottom: 0, color: '#333', fontSize: '0.9rem' },
  eventsSection: { maxWidth: 1180, margin: '0 auto', padding: '4rem 1.5rem 4rem' }, sectionTop: { display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: '1.2rem' }, eyebrow: { color: '#111', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: '0.72rem', margin: 0 }, sectionTitle: { fontSize: 'clamp(2rem, 4vw, 3.25rem)', letterSpacing: '-0.05em', margin: '0.35rem 0 0', lineHeight: 1 },
  controls: { display: 'flex', gap: 8, flexWrap: 'wrap' }, searchBox: { display: 'flex', alignItems: 'center', gap: 7, background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: '0 0.7rem', color: '#555' }, searchInput: { border: 0, outline: 0, background: 'transparent', color: '#111', width: 150, font: 'inherit', padding: '0.7rem 0' }, selectBox: { position: 'relative' }, selectButton: { minWidth: 148, background: '#fff', color: '#111', border: '1px solid #ccc', borderRadius: 8, padding: '0.7rem 0.75rem', cursor: 'pointer', font: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }, selectMenu: { position: 'absolute', left: 0, top: 'calc(100% + 6px)', zIndex: 20, minWidth: '100%', overflow: 'hidden', background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 4, boxShadow: '0 12px 30px rgba(0,0,0,0.12)' }, selectOption: { width: '100%', background: 'transparent', border: 0, borderRadius: 5, color: '#111', textAlign: 'left', padding: '0.6rem 0.7rem', cursor: 'pointer', font: 'inherit', whiteSpace: 'nowrap' }, selectOptionActive: { color: '#fff', background: '#111', fontWeight: 700 }, srOnly: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '2rem' }, chip: { background: '#fff', color: '#333', border: '1px solid #ccc', borderRadius: 999, padding: '0.45rem 0.85rem', cursor: 'pointer', font: 'inherit', fontSize: '0.85rem' }, chipActive: { background: '#111', color: '#fff', borderColor: '#111', fontWeight: 700 },
  month: { marginBottom: '2.6rem' }, monthTitle: { fontSize: '1.15rem', letterSpacing: '-0.02em', margin: '0 0 1rem', paddingBottom: '0.6rem', borderBottom: '1px solid #e5e5e5' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(265px, 1fr))', gap: 14 },
  status: { color: '#555', padding: '2rem 0' }, empty: { textAlign: 'center', background: '#fafafa', border: '1px dashed #bbb', borderRadius: 14, padding: '4rem 1.5rem', color: '#444' }, emptyLink: { color: '#111', display: 'inline-flex', gap: 5, alignItems: 'center', marginTop: 10, textDecoration: 'none', fontWeight: 700 }, emptyButton: { background: '#111', color: '#fff', border: 0, borderRadius: 7, padding: '0.6rem 0.9rem', cursor: 'pointer', font: 'inherit', fontWeight: 700, marginTop: 10 },
};
